// The cue of a practice question: labelled facts (कर्ता + धातु + काल = ?), a Prakrit or Sanskrit word shown large,
// or a meaning to translate. Prakrit stays plain text until the question is answered, so the Word Explorer cannot
// give the answer away; afterwards every word is tappable. A विकल्प (variant) cue form carries the विकल्प badge.

import type { FC, ReactNode } from 'react'
import type { QuestionCue } from '@/prakrit/components/practice/practice-types'

import { Fragment } from 'react'
import { Equal, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { StatusBadge } from '@/prakrit/components/common/status-badge'

interface QuestionCueViewProps {
    cue: QuestionCue
    /** The question has been answered: Prakrit becomes tappable. */
    settled: boolean
    kids: boolean
}

export const QuestionCueView: FC<QuestionCueViewProps> = ({ cue, settled, kids }): ReactNode => {
    const plus = cue.join === 'plus'
    const big = cue.pra ?? cue.sa
    const chips = cue.chips ?? []

    return (
        <div className={cn('flex flex-col items-center gap-4 rounded-2xl border border-border bg-card px-4 py-5 text-center shadow-card sm:px-6', kids && 'border-saffron/40')}>
            {chips.length > 0 && (
                <ul className='flex flex-wrap items-stretch justify-center gap-2'>
                    {chips.map((chip, i) => (
                        <Fragment key={i}>
                            {plus && i > 0 && (
                                <li aria-hidden='true' className='flex items-center text-muted-foreground'>
                                    <Plus className='size-4' />
                                </li>
                            )}
                            <li className='flex min-w-24 flex-col items-center gap-0.5 rounded-xl bg-surface-muted px-3 py-2'>
                                <span className='text-xs font-semibold text-muted-foreground'>
                                    <span lang='hi' className='hindi'>{chip.label.hi}</span>
                                    <span aria-hidden='true'> · </span>
                                    <span lang='en'>{chip.label.en}</span>
                                </span>
                                {chip.pra && (
                                    <span className='pra pra-md'>
                                        <PrakritText text={chip.pra} plain={!settled} />
                                    </span>
                                )}
                                {(chip.hi || chip.en) && (
                                    <Bi
                                        hi={chip.hi ?? ''}
                                        en={chip.en ?? ''}
                                        layout={chip.pra ? 'inline' : 'stack'}
                                        className={cn('items-center justify-center text-sm', !chip.pra && 'font-medium')}
                                        hiClassName={chip.pra ? undefined : 'text-base'}
                                    />
                                )}
                            </li>
                        </Fragment>
                    ))}
                    {plus && (
                        <>
                            <li aria-hidden='true' className='flex items-center text-muted-foreground'>
                                <Equal className='size-4' />
                            </li>
                            <li aria-hidden='true' className='flex min-w-16 items-center justify-center rounded-xl border-2 border-dashed border-saffron/60 px-3 py-2 text-2xl font-bold text-saffron-strong'>
                                ?
                            </li>
                        </>
                    )}
                </ul>
            )}

            {cue.pra && (
                <p className={cn('pra', kids ? 'pra-display' : 'pra-xl')}>
                    <PrakritText text={cue.pra} plain={!settled} />
                </p>
            )}

            {cue.pra && cue.vikalp && (
                <p className='flex flex-wrap items-center justify-center gap-2 text-sm'>
                    <StatusBadge kind='alt' />
                    <Bi hi='यह विकल्प रूप है' en='This is a variant form' layout='inline' enClassName='text-muted-foreground' />
                </p>
            )}

            {cue.sa && (
                <p lang='sa' className={cn('font-prakrit font-semibold text-foreground', kids ? 'text-5xl' : 'text-4xl')}>{cue.sa}</p>
            )}

            {(cue.hi || cue.en) && (
                big ? (
                    <Bi hi={cue.hi ?? ''} en={cue.en ?? ''} layout='inline' className='justify-center text-base' />
                ) : (
                    <p className='flex flex-col items-center gap-1'>
                        <span lang='hi' data-ex='hi' className={cn('hindi font-semibold text-foreground', kids ? 'text-3xl' : 'text-2xl')}>{cue.hi}</span>
                        <span lang='en' data-ex='en' className='text-lg text-muted-foreground'>{cue.en}</span>
                    </p>
                )
            )}

            {cue.note && (
                <p className='text-sm text-muted-foreground'>
                    <Bi hi={cue.note.hi} en={cue.note.en} />
                </p>
            )}
        </div>
    )
}
